import Seo from "../components/Seo";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Flower2, HandCoins, HeartHandshake, Home as HomeIcon, ShieldCheck, Users } from "lucide-react";
import { IMAGES } from "../lib/site";
import { FadeIn } from "../components/FadeIn";
import PageHeader from "../components/PageHeader";

const values = [
  {
    icon: HomeIcon,
    title: "A home away from home",
    desc: "Children spend their mornings and afternoons with us, so we design every routine, space and interaction to feel safe, familiar and warm - like home, not an extension of the classroom.",
  },
  {
    icon: HeartHandshake,
    title: "Relationships first",
    desc: "Secure, trusting relationships between children, educators and families are the foundation of wellbeing and learning. We take time to know every child and every family.",
  },
  {
    icon: Users,
    title: "Community & belonging",
    desc: "As part of the Telopea Park School community in Barton, we celebrate the diversity of our families and nurture each child's sense of belonging.",
  },
  {
    icon: Flower2,
    title: "Play-based learning",
    desc: "Guided by the My Time, Our Place framework, our programs grow from children's interests - balancing active play, creativity, rest and friendship.",
  },
];

const missionPoints = [
  "Deliver high quality care.",
  "Maintain continuity of caring staff.",
  "Build strong relationships with families and students.",
  "Align with school and family values.",
  "Connect with the Telopea community.",
  "Respond to family and student needs.",
];

const governanceStats = [
  {
    icon: HandCoins,
    stat: "100%",
    label: "Not-for-profit \u2013 every dollar reinvested into the service, never shareholder profit",
  },
  {
    icon: Users,
    stat: "Parent-led",
    label: "Governed by a volunteer P&C committee of local Telopea Park families",
  },
  {
    icon: HeartHandshake,
    stat: "~6 in 10",
    label: "Australian OSHC services are run by not-for-profit & community organisations, not private operators",
  },
  {
    icon: ShieldCheck,
    stat: "Exceeding",
    label: "Rated Exceeding the National Quality Standard across our assessed quality areas",
  },
];

const About = () => (
  <div data-testid="about-page">
    <Seo
      title="About & Philosophy | Telopea Park OSHC"
      description="Telopea Park OSHC is a not-for-profit, parent-committee (P&C) run OSHC in Barton, Canberra. Read our story, philosophy and community-run values."
      path="/about"
    />
    <PageHeader
      testId="about-header"
      eyebrow="About & Philosophy"
      title="A Home Away From Home in the heart of Barton"
      description="Telopea Park Out of School Hours Care has long served families of Telopea Park School and the wider Canberra community with warm, professional care before school, after school and in the holidays."
    />

    <section className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-24" data-testid="about-story">
      <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
        <FadeIn>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-navy" data-testid="about-story-title">
            Our story
          </h2>
          <div className="mt-6 space-y-5 text-lg text-slate-600 leading-relaxed">
            <p>
              Located on the grounds of Telopea Park School on New South Wales Crescent, our service cares for school-age
              children in a dedicated, welcoming space that children genuinely look forward to coming to.
            </p>
            <p>
              We believe out of school hours care should be more than supervision. It is time children get to call their
              own - to play, create, rest, explore and build friendships, supported by educators who know them well.
            </p>
            <p>
              Our name honours the waratah (<em>Telopea speciosissima</em>) - a flower that thrives with strong roots and
              the right care. It is a daily reminder of how we see every child in our service.
            </p>
          </div>
        </FadeIn>
        <FadeIn delay={0.1}>
          <img
            src={IMAGES.sandbox}
            alt="Children playing a parachute game together on the school grounds"
            className="w-full rounded-3xl object-cover shadow-lg aspect-[4/3]"
            data-testid="about-story-image"
          />
        </FadeIn>
      </div>
    </section>

    <section className="bg-navy" data-testid="about-governance">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-24">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-start">
          <FadeIn>
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-waratah-300">Community-run &amp; not-for-profit</p>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-white" data-testid="about-governance-title">
              OSHC &amp; the P&amp;C
            </h2>
            <div className="mt-6 space-y-5 text-lg text-navy-100 leading-relaxed">
              <p>
                Telopea Park School&rsquo;s Outside School Hours Care (OSHC) program is one of the few services in Canberra still
                managed directly by the <strong className="text-white">Parents &amp; Citizens (P&amp;C) Association</strong>. This
                means our not-for-profit service is shaped by the priorities, values and expectations of Telopea&rsquo;s parents
                and community &ndash; giving them a direct voice in the operations of the program. Parents in the P&amp;C are
                involved in setting the priorities of the program, identifying improvements and supporting our teams.
              </p>
              <p className="font-semibold text-white">
                Our mission is to serve the school community, and as a not-for-profit we are uniquely placed to:
              </p>
            </div>
            <ul className="mt-6 space-y-3" data-testid="about-mission-list">
              {missionPoints.map((m) => (
                <li key={m} className="flex items-start gap-3">
                  <Check className="mt-1 h-5 w-5 flex-none text-waratah-300" />
                  <span className="text-lg leading-relaxed text-navy-100">{m}</span>
                </li>
              ))}
            </ul>
          </FadeIn>
          <FadeIn delay={0.1}>
            <div className="grid gap-5 sm:grid-cols-2">
              {governanceStats.map((s, i) => (
                <div
                  key={s.stat}
                  className="rounded-2xl bg-white/10 p-6 ring-1 ring-white/15 backdrop-blur"
                  data-testid={`about-governance-stat-${i + 1}`}
                >
                  <s.icon className="h-7 w-7 text-waratah-300" />
                  <p className="mt-4 font-display text-3xl font-bold text-white">{s.stat}</p>
                  <p className="mt-2 text-sm text-navy-100 leading-relaxed">{s.label}</p>
                </div>
              ))}
            </div>
          </FadeIn>
        </div>
      </div>
    </section>

    <section className="bg-surface border-y border-navy-100" data-testid="about-philosophy">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-24">
        <FadeIn>
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-waratah">Our Philosophy</p>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-navy max-w-2xl">
            What guides us every day
          </h2>
        </FadeIn>
        <div className="mt-12 grid gap-6 md:grid-cols-2">
          {values.map((v, i) => (
            <FadeIn key={v.title} delay={i * 0.08}>
              <div className="h-full rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-testid={`about-value-${i + 1}`}>
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-waratah-50 text-waratah">
                  <v.icon className="h-6 w-6" />
                </div>
                <h3 className="mt-6 font-display text-2xl font-semibold text-navy">{v.title}</h3>
                <p className="mt-3 text-base text-slate-600 leading-relaxed">{v.desc}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>

    <section className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-24" data-testid="about-country">
      <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
        <FadeIn className="order-2 lg:order-1">
          <img
            src={IMAGES.waratah}
            alt="A red waratah flower in bloom"
            className="w-full rounded-3xl object-cover shadow-lg aspect-[4/3]"
            data-testid="about-waratah-image"
          />
        </FadeIn>
        <FadeIn delay={0.1} className="order-1 lg:order-2">
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-waratah">Acknowledgement of Country</p>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-navy" data-testid="about-country-title">
            With respect to the Ngunnawal people
          </h2>
          <div className="mt-6 space-y-5 text-lg text-slate-600 leading-relaxed" data-testid="about-acknowledgement">
            <p>
              Telopea Park OSHC acknowledges the Ngunnawal people as the Traditional Custodians of the land on which our
              service learns and plays. We pay our respects to Elders past and present, and extend that respect to all
              Aboriginal and Torres Strait Islander peoples in our community.
            </p>
            <p>
              We are committed to embedding Aboriginal and Torres Strait Islander perspectives in our program in ways
              that are meaningful, respectful and developed in partnership.
            </p>
          </div>
          <Link
            to="/nqs"
            data-testid="about-nqs-link"
            className="mt-8 inline-flex items-center gap-2 rounded-full border-2 border-navy px-7 py-3.5 text-base font-semibold text-navy transition-all duration-200 hover:-translate-y-0.5 hover:bg-navy hover:text-white"
          >
            See our quality ratings <ArrowRight className="h-4 w-4" />
          </Link>
        </FadeIn>
      </div>
    </section>
  </div>
);

export default About;
